import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Globe, Facebook, Instagram } from 'lucide-react';
import { contatti as contattiMock, siteInfo } from '../data/mock';
import { api } from '../api/client';
import { useSiteText } from '../hooks/useSiteText';

// Footer completamente white-label:
//  - logo caricato dalle settings (con cache localStorage anti-flash)
//  - 3 righe testo, descrizione, copyright, hashtag: editabili via admin Site Texts
//  - Facebook + Instagram: solo se URL presente nel DB contatti
export default function Footer() {
  const cachedLogo = (typeof window !== 'undefined') ? (window.localStorage.getItem('cachedLogoUrl') || '') : '';
  const [logoUrl, setLogoUrl] = useState(cachedLogo || siteInfo.logo);
  const [logoBroken, setLogoBroken] = useState(false);
  const [contatti, setContatti] = useState(contattiMock);

  // Chiavi di testo editabili — condivise dove possibile con l'Header
  const logoLine1 = useSiteText('header.logo.line1', 'A.S.D.P.');
  const logoLine2 = useSiteText('header.logo.line2', 'Primavera');
  const logoLine3 = useSiteText('header.logo.line3', 'Cavese');
  const descr = useSiteText('footer.description', 'Associazione Sportiva Dilettantistica Polisportiva — Organizzatori del Memorial "Eugenio Abbro" dal 2005.');
  const copyright = useSiteText('footer.copyright', `© ${new Date().getFullYear()} A.S.D.P. Primavera Cavese — Tutti i diritti riservati.`);
  const hashtag = useSiteText('footer.hashtag', '#MemorialEugenioAbbro — Sport • Giovani • Passione');
  const websiteLabel = useSiteText('footer.website_label', 'www.memorialeugenioabbro.it');
  const contattiTitle = useSiteText('footer.contatti_title', 'Contatti');
  const linkTitle = useSiteText('footer.link_title', 'Link utili');
  const infoLabel = useSiteText('footer.info_label', 'Info');

  useEffect(() => {
    let alive = true;
    api.getSettings().then((s) => {
      if (!alive) return;
      if (s?.logo_url) {
        setLogoUrl(s.logo_url);
        setLogoBroken(false);
      }
    }).catch(() => {});
    api.getContatti().then((c) => {
      if (!alive || !c) return;
      setContatti({
        email: c.email || contattiMock.email,
        phones: (c.phones && c.phones.length > 0) ? c.phones : contattiMock.phones,
        address: c.address || contattiMock.address,
        facebook: c.facebook_url || '',
        instagram: c.instagram_url || '',
        website: contattiMock.website,
      });
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const line = (s) => s || '';
  const firstChar = (s) => line(s).charAt(0);
  const restChars = (s) => line(s).slice(1);

  return (
    <footer className="bg-navy-dark pt-16 pb-0 mt-20" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 pb-12">
          <div className="flex flex-col items-start">
            <div className="flex items-center gap-4 mb-4">
              <img
                src={logoBroken ? siteInfo.logo : logoUrl}
                alt="logo"
                className="w-20 h-20 object-contain"
                onError={() => setLogoBroken(true)}
                data-testid="footer-logo-img"
              />
              <div className="leading-tight">
                <div className="text-[10px] font-bold text-pink-brand uppercase tracking-widest" data-testid="footer-logo-line1">{logoLine1}</div>
                <div className="font-brand text-xl font-bold text-white" data-testid="footer-logo-line2">
                  {firstChar(logoLine2)}<span className="text-pink-brand">{restChars(logoLine2)}</span>
                </div>
                <div className="font-brand text-xl font-bold text-white -mt-1" data-testid="footer-logo-line3">
                  {firstChar(logoLine3)}<span className="text-pink-brand">{restChars(logoLine3)}</span>
                </div>
              </div>
            </div>
            <p className="text-white/70 text-sm max-w-xs leading-relaxed" data-testid="footer-description">
              {descr}
            </p>
            <div className="mt-5 flex gap-3">
              {contatti.facebook && (
                <a
                  href={contatti.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-pink-brand flex items-center justify-center hover:scale-110 transition-transform"
                  aria-label="Facebook"
                  data-testid="footer-facebook-link"
                >
                  <Facebook size={22} className="text-white" />
                </a>
              )}
              {contatti.instagram && (
                <a
                  href={contatti.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-gradient-to-br from-[#f09433] via-[#dc2743] to-[#833ab4] flex items-center justify-center hover:scale-110 transition-transform"
                  aria-label="Instagram"
                  data-testid="footer-instagram-link"
                >
                  <Instagram size={22} className="text-white" />
                </a>
              )}
            </div>
          </div>

          <div>
            <h3 className="font-display text-xl font-bold text-white uppercase mb-5 tracking-wider">{contattiTitle}</h3>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 bg-pink-brand rounded-md flex items-center justify-center flex-shrink-0">
                  <Mail size={16} className="text-white" />
                </span>
                <a href={`mailto:${contatti.email}`} className="text-white/85 hover:text-white mt-1">
                  {contatti.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 bg-pink-brand rounded-md flex items-center justify-center flex-shrink-0">
                  <Phone size={16} className="text-white" />
                </span>
                <div className="text-white/85 mt-1">
                  {infoLabel}: {(contatti.phones || []).join(' · ')}
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 bg-pink-brand rounded-md flex items-center justify-center flex-shrink-0">
                  <MapPin size={16} className="text-white" />
                </span>
                <span className="text-white/85 mt-1">{contatti.address}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 bg-pink-brand rounded-md flex items-center justify-center flex-shrink-0">
                  <Globe size={16} className="text-white" />
                </span>
                <a href={contatti.website} className="text-white/85 hover:text-white mt-1" data-testid="footer-website-link">
                  {websiteLabel}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-display text-xl font-bold text-white uppercase mb-5 tracking-wider">{linkTitle}</h3>
            <ul className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {[
                ['/', 'Home'],
                ['/chi-siamo', 'Chi Siamo'],
                ['/squadre', 'Squadre'],
                ['/calendario', 'Calendario'],
                ['/classifiche', 'Classifiche'],
                ['/regolamento', 'Regolamento'],
                ['/edizioni-passate', 'Edizioni Passate'],
                ['/contatti', 'Contatti'],
              ].map(([to, label]) => (
                <li key={to}>
                  <Link to={to} className="text-white/85 hover:text-pink-brand transition-colors">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10"></div>
        <div className="py-5 flex flex-col sm:flex-row justify-between items-center gap-2 text-xs text-white/60">
          <span data-testid="footer-copyright">{copyright}</span>
          <Link to="/admin" className="hover:text-pink-brand transition-colors">
            Area Riservata
          </Link>
        </div>
      </div>
      <div className="bg-pink-brand py-3 text-center text-white text-sm font-semibold tracking-wider" data-testid="footer-hashtag">
        {hashtag}
      </div>
    </footer>
  );
}
